import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import WebViewer, { WebViewerInstance } from '@pdftron/webviewer'

import { useWindowSize } from '@utils/hooks'

import { ApryseConfig, CustomMarkerButton, MarkerActions } from '../type'

import {
  CSS_FILE_DEFAULT_PATH,
  MARKER_CONTAINER_ID,
  UI_CONFIG_ADVANCED_PATH,
  UI_CONFIG_MOBILE_PATH,
  UI_CONFIG_SIMPLE_PATH,
  WEB_VIEWER_PATH,
} from '../constants'

interface UsePdfViewerProps {
  pdfUrl: string
  viewerRef: React.RefObject<HTMLDivElement>
  config: ApryseConfig
  initialXFdf?: string
  markerActions?: MarkerActions
  customMarkerButton?: CustomMarkerButton
  onXFdfChanged?: (xfdf: string) => void
}

interface UsePdfViewerReturn {
  isViewerInitialized: boolean
  instanceRef: React.MutableRefObject<WebViewerInstance | null>
  pageNumber: number
  zoomLevel: number
  markerContainer: HTMLElement | null
  isPageReady: boolean
  reloadDocument: (url: string) => void
}

export const usePdfViewer = ({
  pdfUrl,
  viewerRef,
  initialXFdf,
  config,
  markerActions,
  customMarkerButton,
  onXFdfChanged,
}: UsePdfViewerProps): UsePdfViewerReturn => {
  const isViewerInitialized = useRef<boolean>(false)
  const isLoadingRef = useRef(false)
  const instanceRef = useRef<WebViewerInstance | null>(null)
  const [isPageReady, setIsPageReady] = useState<boolean>(false)

  const [pageNumber, setPageNumber] = useState<number>(1)
  const [zoomLevel, setZoomLevel] = useState<number>(1)
  const [markerContainer, setMarkerContainer] = useState<HTMLElement | null>(null)
  const { isMobile } = useWindowSize()

  const {
    i18n: { language },
  } = useTranslation()

  const importAnnotations = async (instance: WebViewerInstance) => {
    if (!initialXFdf) return
    await instance.Core.annotationManager.importAnnotations(initialXFdf)
  }

  const addMarkersOverlay = () => {
    if (!instanceRef.current) return

    const container = document.createElement('div')
    container.id = MARKER_CONTAINER_ID

    const viewer = instanceRef.current.UI.iframeWindow.document.querySelector<HTMLElement>('.document')
    if (viewer) {
      viewer.appendChild(container)
      setMarkerContainer(container)
    }
  }

  const applyCustomUI = async (instance: WebViewerInstance) => {
    // LANGUAGE
    instance.UI.setLanguage(language)

    // HEADER
    if (customMarkerButton && markerActions && config.UIMode === 'advanced') {
      const button = new instance.UI.Components.StatefulButton({
        initialState: 'visible',
        states: {
          visible: {
            img: customMarkerButton.showIcon,
            title: customMarkerButton.translations.hide,
            onClick: (update: (state: 'visible' | 'hidden') => void) => {
              if (markerActions.isAdding()) return

              markerActions.hideMarkers()
              update('hidden')
            },
          },
          hidden: {
            img: customMarkerButton.hideIcon,
            title: customMarkerButton.translations.show,
            onClick: (update: (state: 'visible' | 'hidden') => void) => {
              if (markerActions.isAdding()) return

              markerActions.showMarkers()
              update('visible')
            },
          },
        },
      })

      const header = instance.UI.getModularHeader('default-top-header')
      const items = header.getItems()

      const newItems = items[0] as any
      newItems.items = [...newItems.items, button]

      header.setItems(items)
    }

    if (config.disabledElements) {
      instance.UI.disableElements(config.disabledElements)
    }
  }

  const setDisplayMode = async (instance: WebViewerInstance) => {
    const { documentViewer, DisplayModes, VirtualDisplayMode } = instance.Core

    const displayMode = new VirtualDisplayMode(documentViewer, DisplayModes.Single)
    const modeManager = documentViewer.getDisplayModeManager()

    modeManager.setDisplayMode(displayMode)
  }

  const handleDocumentLoaded = async (instance: WebViewerInstance) => {
    const { documentViewer, Tools } = instance.Core

    documentViewer.addEventListener('documentLoaded', async () => {
      isLoadingRef.current = false

      instance.UI.setToolMode(Tools.ToolNames.PAN)

      addMarkersOverlay()
      setDisplayMode(instance)
      importAnnotations(instance)

      if (config.fitMode) {
        setFitMode(instance, config.fitMode)
      }
    })
  }

  const setFitMode = async (instance: WebViewerInstance, fitMode: 'FitWidth' | 'FitPage') => {
    const { documentViewer } = instance.Core
    const { FitMode } = documentViewer

    const newFitMode = fitMode === 'FitWidth' ? FitMode.FitWidth : FitMode.FitPage

    documentViewer.setFitMode(newFitMode)
  }

  const handleClick = () => {
    if (markerActions) {
      instanceRef?.current?.UI.iframeWindow.document
        .getRootNode()
        .addEventListener('click', () => markerActions.setSelectedMarkerId(null))
    }
  }

  const handleZoomUpdated = async (instance: WebViewerInstance) => {
    const { documentViewer } = instance.Core

    documentViewer.addEventListener('zoomUpdated', async (zoom: number) => {
      setZoomLevel(zoom)
    })
  }

  const handleAnnotationChanged = async (instance: WebViewerInstance) => {
    const { annotationManager } = instance.Core

    annotationManager.addEventListener('annotationChanged', async (_, __, options) => {
      if (options.imported) return

      const xfdf = await annotationManager.exportAnnotations()
      onXFdfChanged && onXFdfChanged(xfdf)
    })
  }

  const handlePageChanged = async (instance: WebViewerInstance) => {
    const { documentViewer } = instance.Core

    documentViewer.addEventListener('pageNumberUpdated', (pageNumber: number) => {
      setPageNumber(pageNumber)
    })
  }

  const handlePageCompleted = async (instance: WebViewerInstance) => {
    const { documentViewer } = instance.Core

    documentViewer.addEventListener('pageComplete', () => {
      setIsPageReady(true)
    })
  }

  useEffect(() => {
    if (!viewerRef.current || isViewerInitialized.current) return

    isViewerInitialized.current = true

    const mapConfigUI = {
      simple: UI_CONFIG_SIMPLE_PATH,
      advanced: UI_CONFIG_ADVANCED_PATH,
      mobile: UI_CONFIG_MOBILE_PATH,
    }

    const uiConfig = mapConfigUI[isMobile ? 'mobile' : config.UIMode || 'advanced']
    const enableMeasurement = !isMobile

    WebViewer.Iframe(
      {
        licenseKey: import.meta.env.VITE_FEATURE_APRYSE_API_KEY,
        path: WEB_VIEWER_PATH,
        css: CSS_FILE_DEFAULT_PATH,
        isReadOnly: config.isReadOnly,
        uiConfig: uiConfig,
        disabledElements: ['pageNavOverlay'],
        disableLogs: true,
        enableMeasurement,
      },
      viewerRef.current,
    ).then(async (instance: WebViewerInstance) => {
      setIsPageReady(false)

      instance.UI.loadDocument(pdfUrl, {
        customHeaders: {
          'Cache-Control': 'no-cache',
          pragma: 'no-cache',
        },
      })

      instanceRef.current = instance

      applyCustomUI(instance)

      handleDocumentLoaded(instance)
      handleAnnotationChanged(instance)
      handlePageChanged(instance)
      handlePageCompleted(instance)
      handleZoomUpdated(instance)
      handleClick()
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reloadDocument = async (url: string) => {
    if (!instanceRef.current || isLoadingRef.current) return

    setIsPageReady(false)

    const { UI } = instanceRef.current

    isLoadingRef.current = true

    UI.loadDocument(url)
  }

  return {
    isViewerInitialized: isViewerInitialized.current,
    instanceRef,
    pageNumber,
    zoomLevel,
    markerContainer,
    isPageReady,
    reloadDocument,
  }
}
